import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumn } from '@saas-kit/ui/DataTable';

import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import type { AdminInvoice } from './types';

interface Options {
  onPick: (invoice: AdminInvoice) => void;
}

export function useInvoiceColumns({
  onPick,
}: Options): DataTableColumn<AdminInvoice>[] {
  const { t } = useTranslation('adminBilling');
  return useMemo<DataTableColumn<AdminInvoice>[]>(
    () => [
      {
        id: 'invoice_number',
        header: t('table.invoice_number'),
        cell: (row) => (
          <button
            type="button"
            className="text-primary underline-offset-2 hover:underline"
            onClick={() => onPick(row)}
          >
            {row.invoice_number}
          </button>
        ),
      },
      {
        id: 'status',
        header: t('table.status'),
        cell: (row) => <InvoiceStatusBadge status={row.status} />,
        exportValue: (row) => row.status,
      },
      {
        id: 'total',
        header: t('table.total'),
        align: 'end',
        cell: (row) => `${row.total_amount} ${row.currency}`,
        exportValue: (row) => `${row.total_amount} ${row.currency}`,
      },
      {
        id: 'amount_paid',
        header: t('table.paid'),
        align: 'end',
        cell: (row) => `${row.amount_paid} ${row.currency}`,
        exportValue: (row) => `${row.amount_paid} ${row.currency}`,
      },
      {
        id: 'due_at',
        header: t('table.due_at'),
        cell: (row) =>
          row.due_at ? new Date(row.due_at).toLocaleDateString() : '—',
        exportValue: (row) => row.due_at ?? '',
        hideOnMobile: true,
      },
      {
        id: 'issued_at',
        header: t('table.issued_at'),
        cell: (row) =>
          row.issued_at ? new Date(row.issued_at).toLocaleDateString() : '—',
        exportValue: (row) => row.issued_at ?? '',
        hideOnMobile: true,
      },
    ],
    [t, onPick],
  );
}
